'use client'

import { useRef, useState, type ReactNode } from 'react'
import { ArrowDown, ChevronDown } from '@/components/ui/icons'

// The agent's note: its closing line as the display quote, three lines lifted from it, and the
// whole note underneath. Each lifted line opens the note at the paragraph it came from and marks
// it there, so a reader can check the quote in context. The full text is always in the markup
// (native <details>), for crawlers and for no JS.
export function AgentNote({
  paragraphs,
  lines,
  close,
  author,
  credit,
}: {
  paragraphs: string[]
  lines: string[]
  close: string
  author: string
  credit: string
}) {
  const detailsRef = useRef<HTMLDetailsElement>(null)
  const [open, setOpen] = useState(false)
  const [active, setActive] = useState<number | null>(null)
  const paragraphOf = (line: string) => paragraphs.findIndex(p => p.includes(line))

  const showInContext = (i: number) => {
    const details = detailsRef.current
    if (!details) return
    details.open = true
    setActive(i)
    // After the note has opened and laid out.
    requestAnimationFrame(() =>
      document.getElementById(`agent-note-line-${i}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' }),
    )
  }

  // A paragraph with any lifted lines wrapped in a mark.
  const withMarks = (para: string): ReactNode => {
    const parts: ReactNode[] = []
    let rest = para
    lines.forEach((line, i) => {
      const at = rest.indexOf(line)
      if (at < 0) return
      parts.push(rest.slice(0, at))
      parts.push(
        <mark
          key={i}
          id={`agent-note-line-${i}`}
          className={`scroll-mt-32 rounded-[3px] px-0.5 text-white transition-colors duration-500 ${
            active === i ? 'bg-white/25' : 'bg-white/[0.08]'
          }`}
        >
          {line}
        </mark>,
      )
      rest = rest.slice(at + line.length)
    })
    parts.push(rest)
    return parts
  }

  return (
    <figure className="mt-8 flex w-full flex-col items-center">
      {/* Repeats the note's last two sentences, so it is hidden from screen readers. */}
      <blockquote
        aria-hidden
        className="relative max-w-[1040px] text-balance text-center text-[34px] font-semibold leading-[1.08] tracking-[-0.035em] text-white md:text-[60px]"
      >
        <span className="text-white/25">“</span>
        {close}
        <span className="text-white/25">”</span>
      </blockquote>
      <figcaption className="mt-8 flex items-center gap-3 rounded-full border border-white/[0.09] bg-white/[0.03] py-2 pe-5 ps-2 text-start">
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-white/10">
          {/* eslint-disable-next-line @next/next/no-img-element -- static brand mark */}
          <img src="/marketing/logos-mono/claude.svg" alt="" className="h-5 w-5" />
        </span>
        <span className="text-[14px] leading-tight">
          <span className="block font-medium text-white/90">{author}</span>
          <span className="block text-white/45">{credit}</span>
        </span>
      </figcaption>

      <ul className="mt-14 grid w-full gap-4 md:grid-cols-3">
        {lines.map((line, i) => (
          <li key={line}>
            <button
              type="button"
              onClick={() => showInContext(i)}
              className="group flex h-full w-full flex-col rounded-2xl border border-white/[0.09] bg-white/[0.02] p-6 text-start transition-colors hover:border-white/25 hover:bg-white/[0.05] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/40"
            >
              <span className="text-[19px] font-medium leading-snug tracking-[-0.015em] text-white/90">“{line}”</span>
              <span className="mt-auto flex items-center gap-1.5 pt-6 text-[13px] text-white/45 transition-colors group-hover:text-white/80">
                Paragraph {paragraphOf(line) + 1} · Read it in context
                <ArrowDown className="h-3.5 w-3.5 transition-transform duration-150 group-hover:translate-y-0.5" />
              </span>
            </button>
          </li>
        ))}
      </ul>

      <details
        ref={detailsRef}
        onToggle={e => {
          const isOpen = e.currentTarget.open
          setOpen(isOpen)
          if (!isOpen) setActive(null)
        }}
        className="group mt-8 flex w-full flex-col items-center"
      >
        <summary className="mx-auto inline-flex cursor-pointer list-none items-center gap-2 rounded-full border border-white/20 px-5 py-2.5 text-[15px] font-medium text-white transition-colors hover:border-white/40 hover:bg-white/[0.06] [&::-webkit-details-marker]:hidden">
          {open ? 'Hide the note' : `Read the full note · ${paragraphs.length} paragraphs`}
          <ChevronDown className="h-4 w-4 text-white/50 transition-transform duration-200 group-open:rotate-180" />
        </summary>
        <blockquote className="mx-auto mt-8 w-full max-w-[760px] rounded-2xl border border-white/[0.09] bg-white/[0.02] px-6 py-8 md:px-10 md:py-10">
          <p className="text-[24px] font-semibold leading-[1.2] tracking-[-0.025em] text-white md:text-[30px]">
            {withMarks(paragraphs[0])}
          </p>
          <div className="mt-8 space-y-6 text-[17px] leading-relaxed text-white/70">
            {paragraphs.slice(1).map(para => (
              <p key={para.slice(0, 32)}>{withMarks(para)}</p>
            ))}
          </div>
          <p className="mt-8 text-[14px] text-white/45">— {author}</p>
        </blockquote>
      </details>
    </figure>
  )
}
